import {
  GameRangeFilterOption,
  GameType,
  KeysToCamelCase,
  PeriodRange,
  CycleWithRels,
  Cycle,
} from '@statsbomb/parachute-types';
import dayjs from 'dayjs';
import localForage from 'localforage';
import { isEqual } from 'lodash/fp';
import { filterAtomsToHydrateCallback } from '@/consts/atomHydration';
import { filterAtomClearDefaults } from '@/consts/defaults/filterAtomDefaults';
import { TEMPORARY_FILTER_SET_BASE_FILTERS, TEMPORARY_FILTER_SET_ID } from '@/consts/filters/filterSets';
import { FILTER_SET_ID, METRIC_KEY } from '@/consts/searchKeys';
import { FormattedGameWithRels, FormattedGameWithTeam } from '@/types/game';
import { ConfigFilterKey, ConfigFilterSet, UserConfigFilterDefinition } from '@/types/userConfigs';

export const filterGamesByGameType = (games: FormattedGameWithTeam[], gameType: GameType) => {
  if (gameType !== 'HOME_AWAY') {
    return games.filter(({ team: { teamId }, game: { homeTeamId, awayTeamId } }) =>
      gameType === 'HOME' ? teamId === homeTeamId : teamId === awayTeamId,
    );
  }
  return games;
};

export const filterGamesByDateRange = (games: FormattedGameWithRels[], startDate?: string, endDate?: string) => {
  if (!startDate && !endDate) return games;

  return games.filter(({ game: { date } }) => {
    const startDateFormat = dayjs(startDate);
    const endDateFormat = dayjs(endDate);
    const gameDateFormat = dayjs(date);

    if (startDate && !endDate) return gameDateFormat >= startDateFormat;
    if (!startDate && endDate) return gameDateFormat <= endDateFormat;
    return gameDateFormat >= startDateFormat && gameDateFormat <= endDateFormat;
  });
};

export const filterGamesByCompetition = (games: FormattedGameWithRels[], competitionIds: number[]) => {
  if (!competitionIds.length) return games;

  return games.filter(({ cycle: { competitionId } }) => competitionIds.includes(competitionId));
};

export const filterGamesBySeason = (games: FormattedGameWithRels[], seasonIds: number[]) => {
  if (!seasonIds.length) return games;

  return games.filter(({ cycle: { seasonId } }) => seasonIds.includes(seasonId));
};

export const filterGamesByTeam = (games: FormattedGameWithTeam[], teamIds: number[]) => {
  if (!teamIds.length) return games;

  return games.filter(({ team: { teamId } }) => teamIds.includes(teamId));
};

export const pipeFilterFunctions =
  <T>(...funcs: Function[]) =>
  (data: T[]) =>
    funcs.reduce((acc, func) => func(acc), data);

export const filterGamesBySelectedGameRange = (
  games: FormattedGameWithRels[],
  selectedGameRange: GameRangeFilterOption,
  periodRange: PeriodRange,
  dateRangeStart?: string,
  dateRangeEnd?: string,
) => {
  if (selectedGameRange.key === 'period') {
    if (periodRange.translation.key === 'lastXMonths') {
      const monthsToSubtract = periodRange.value;
      const startDate = dayjs().subtract(monthsToSubtract, 'months').format('YYYY-MM-DD');
      const endDate = dayjs().format('YYYY-MM-DD');
      return filterGamesByDateRange(games, startDate, endDate);
    }

    const lastXGamesValue = -periodRange.value;
    return games.slice(lastXGamesValue);
  }

  return filterGamesByDateRange(games, dateRangeStart, dateRangeEnd);
};

export const filterUniqueByProperty = <T, K extends keyof T>(property: K, arr: T[]): T[] =>
  arr.reduce<T[]>((acc, cur) => (acc.some(item => item[property] === cur[property]) ? acc : [...acc, cur]), []);

export const getFilterCount = (filters: Partial<ConfigFilterSet>) =>
  Object.entries(filters).reduce((acc, [key, value]) => {
    // GameRange never counts as that's just a toggle to decide if we're showing the
    // date range or period (last 5, last 10 etc) options
    if (key === 'gameRange') return acc;
    const filterKey = key as ConfigFilterKey;
    const defaultValue = filterAtomClearDefaults[filterKey];
    const hydrateCallback = filterAtomsToHydrateCallback[filterKey];
    // This shouldn't happen, but while reviewing someone had an issue here with a bad filter config
    // so adding this to prevent the whole thing breaking.
    if (!hydrateCallback) {
      // eslint-disable-next-line no-console
      console.error(`unable to find hydrateCallback for filter definition key: ${filterKey}`);
      return acc;
    }
    const hydratedValue = hydrateCallback(value);
    // We only want to count saved filters that are not default values
    return isEqual(hydratedValue, defaultValue) ? acc : acc + 1;
  }, 0);

export const filterCyclesWithRelsByCompetition = (
  cycles: KeysToCamelCase<CycleWithRels>[],
  competitionIds: number[],
) => {
  if (!competitionIds.length) return cycles;

  return cycles.filter(({ competition: { competitionId } }) => competitionIds.includes(competitionId));
};

export const filterCyclesBy = (cycles: Cycle[], selectedIds: number[], key: 'competition_id' | 'season_id') => {
  if (!selectedIds.length) return cycles;

  return cycles.filter(cycle => selectedIds.includes(cycle[key]));
};

export const getTempFilterSet = async (): Promise<UserConfigFilterDefinition | null> => {
  try {
    const tempFilterSet = await localForage.getItem<UserConfigFilterDefinition>(TEMPORARY_FILTER_SET_ID);
    return tempFilterSet;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return null;
  }
};

export const createTempFilterSet = async (filtersToSet: Partial<ConfigFilterSet>): Promise<void> => {
  const tempFilterSet: UserConfigFilterDefinition = {
    configId: TEMPORARY_FILTER_SET_ID,
    hudlUserId: '0',
    type: 'playerDataLockerFilters',
    definition: {
      titleSettings: { title: '', translate: false },
      filters: {
        ...TEMPORARY_FILTER_SET_BASE_FILTERS,
        ...filtersToSet,
      },
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: null,
    permission: 'view',
  };

  try {
    await localForage.setItem(TEMPORARY_FILTER_SET_ID, tempFilterSet);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to create temporary filter set:', error);
    throw error;
  }
};

export const filterSetQueryParams = (filterSetId: string, metric?: string | null) => [
  { key: FILTER_SET_ID, value: filterSetId },
  ...(metric ? [{ key: METRIC_KEY, value: metric }] : []),
];

// once any event filter group applies, the event query drops the flat event names;
// a group's parent event type is the insight key (its event types may not include it)
export const getSelectedEventTypes = ({
  eventNames,
  eventFilterGroups,
}: {
  eventNames?: string[];
  eventFilterGroups?: { parentEventType: string }[];
}) => (eventFilterGroups?.length ? eventFilterGroups.map(group => group.parentEventType) : eventNames ?? []);
